import { Component, inject, Input, OnInit } from '@angular/core';
import { QuantityInputBareChangeResult } from 'projects/shared/lib/components/shared/base/quantity-input/quantity-input-bare/change-result/quantity-input-bare-change-result.model';
import { ToastItem } from 'projects/shared/lib/components/shared/toast/item/toast-item.model';
import { EQuantityValidationResult } from 'projects/shared/lib/enum/quantity/quantity-validation-result.enum';
import { EToastItemType } from 'projects/shared/lib/enum/toast/toast-entry-type.enum';
import { LoggerService } from 'projects/shared/lib/services/infrastructure/logger/logger.service';
import { ServiceLocator } from 'projects/shared/lib/services/infrastructure/service-locator.service';
import { EError, ProductStockInformationGetDto, ShoppingCartDto, ShoppingCartItemDto } from 'projects/shared/lib/services/INTEGRA_Shop_Client';
import { Subject, firstValueFrom } from 'rxjs';
import { ENVIRONMENT } from 'projects/shared/lib/environment/environment';
import { CartPreviewItem } from './cart-preview-item.model';
import { ProductMHService } from 'projects/munhowen/src/app/services/data/product/product.service';
import { ErrorCodeService } from 'projects/shared/lib/services/infrastructure/error-code.service';
import { IConfigurationServiceToken } from 'projects/shared/lib/services/infrastructure/configuration/configuration-service.interface';
import { ICartService, ICartServiceToken } from 'projects/shared/lib/services/data/cart/cart-service.interface';
import { IComparisonService, IComparisonServiceToken } from 'projects/shared/lib/services/infrastructure/comparison/comparison-service.interface';
import { ITooltipService, ITooltipServiceToken } from 'shared/services/ui/tooltip/tooltip-service.interface';
import { IToastService, IToastServiceToken } from 'shared/services/ui/toast/toast-service.interface';

@Component({
  selector: 'app-cart-preview-item',
  templateUrl: './cart-preview-item.component.html',
  standalone: false
})
export class CartPreviewItemMHComponent implements OnInit {

  private logger: LoggerService;
  private readonly cartService: ICartService;
  private readonly toastService: IToastService = inject(IToastServiceToken);
  private readonly toolTipService: ITooltipService = inject(ITooltipServiceToken);
  private readonly comparisonService: IComparisonService;

  @Input() public shouldDisplayValidation: boolean | undefined;
  @Input() public shouldDisableQuantityInput: boolean | undefined;
  @Input() public shouldHideQuantityInput: boolean | undefined;
  @Input() public shouldHidePrice: boolean | undefined;
  @Input() public cartPreviewItem?: CartPreviewItem | undefined;

  public deleteIconTooltipHtmlContent: string | undefined;
  private shouldRemoveProductFromCartUponNegativeQuantity: boolean | undefined;
  public acceptQuantityChangeTrigger: Subject<void>;
  public rejectQuantityChangeTrigger: Subject<void>;

  public constructor() {
    this.logger = ServiceLocator.injector.get(LoggerService);
    this.cartService = inject(ICartServiceToken);
    this.comparisonService = inject(IComparisonServiceToken);

    this.getConfigurationAsync();

    this.acceptQuantityChangeTrigger = new Subject<void>();
    this.rejectQuantityChangeTrigger = new Subject<void>();
  }


  public ngOnInit(): void {
    if (this.cartPreviewItem == undefined) {
      this.logger.info(`Missing model in CartPreviewItem`, this.constructor.name);
      return;
    }

    if (this.cartPreviewItem.productIdentifier == undefined) {
      this.logger.info(`Missing productIdentifier in CartPreviewItem`, this.constructor.name);
      return;
    }
    this.deleteIconTooltipHtmlContent = this.toolTipService.deleteFromCartIconToolTipGet();
  }

  public async getConfigurationAsync() {
    this.shouldRemoveProductFromCartUponNegativeQuantity = await firstValueFrom(ServiceLocator.injector.get(IConfigurationServiceToken).shouldRemoveProductFromCartUponNegativeQuantityGet());
  }

  public quantityChanged(quantityChangeResult: QuantityInputBareChangeResult) {
    if (this.cartPreviewItem == undefined) {
      return;
    }
    if (this.cartPreviewItem.productIdentifier == undefined) {
      return;
    }
    if (this.cartPreviewItem.shoppingCartItem == undefined) {
      return;
    }

    let hasQuantityChanged = this.cartPreviewItem.shoppingCartItem.quantity != quantityChangeResult;
    if (hasQuantityChanged == false) {
      return;
    }

    let cartItemCopy = new ShoppingCartItemDto(this.cartPreviewItem.shoppingCartItem);

    let quantity = quantityChangeResult.valueCurrent;

    switch (quantityChangeResult.validationResult) {
      case undefined:

        break;

      case EQuantityValidationResult.Valid:
        if (quantity == undefined) {
          return;
        }

        cartItemCopy.quantity = quantity;

        this.updateItemInCart(cartItemCopy, undefined);
        break;

      case EQuantityValidationResult.BelowMinium:
        if (this.shouldRemoveProductFromCartUponNegativeQuantity) {
          this.deleteItem();
        }
        break;

      case EQuantityValidationResult.AboveMaximum:
        if (quantity == undefined) {
          return;
        }

        cartItemCopy.quantity = quantity;

        this.updateItemInCart(cartItemCopy, undefined);
        break;

      default:
        break;
    }
  }

  private updateItemInCart(cartItemCopy: ShoppingCartItemDto, skipStockInfo: boolean | undefined) {
    if (skipStockInfo == undefined) {
      skipStockInfo = false;
    }

    if (cartItemCopy == undefined) {
      return;
    }
    if (cartItemCopy.productIdentifier == undefined) {
      return;
    }

    this.cartService.cartItemSetByProductIdentifierAsync(
      undefined,
      cartItemCopy.productIdentifier,
      cartItemCopy.quantity,
      cartItemCopy.orderUnit,
      cartItemCopy.deliveryInformation
    ).then(
      async (shoppingCart: ShoppingCartDto | undefined) => {
        if (shoppingCart) {

          if (!skipStockInfo) {
            let productStockInformationGetItem = new ProductStockInformationGetDto({
              productIdentifier: cartItemCopy.productIdentifier,
              quantityUnit: cartItemCopy.orderUnit?.quantityUnit
            });

            let result = await firstValueFrom(ServiceLocator.injector.get(ProductMHService).productStockInformationGet([productStockInformationGetItem], shoppingCart.deliveryDate));
            let productStockInformationList = result.productStockInformationList;
            let productStockInformation = productStockInformationList?.find(i => this.comparisonService.equalsProductIdentifierByNumberOrGuid(i.productIdentifier, cartItemCopy.productIdentifier));
            if (productStockInformation) {
              if (cartItemCopy.quantity > productStockInformation.availableQuantity) {
                cartItemCopy.quantity = Math.floor(productStockInformation.availableQuantity);
                this.rejectQuantityChangeTrigger.next();
                this.updateItemInCart(cartItemCopy, true);
                return;
              }
            }
          }

          this.acceptQuantityChangeTrigger.next();
          let cartItemNew = shoppingCart.itemList?.find(
            (cartitem) => {
              return this.comparisonService.equalsProductIdentifierByNumberOrGuid(cartItemCopy.productIdentifier, cartitem.productIdentifier);
            }
          );
          if (this.cartPreviewItem != undefined) {
            this.cartPreviewItem.shoppingCartItem = cartItemNew;
            this.cartService.cartCurrentReplaceBy(shoppingCart);
            this.acceptQuantityChangeTrigger.next();
          }
        } else {
          this.rejectQuantityChangeTrigger.next();
        }
      }
    );
  }

  public deleteItem() {
    if (this.cartPreviewItem?.productIdentifier == undefined) {
      return;
    }

    this.cartService.cartItemListDeleteAsync([this.cartPreviewItem.productIdentifier], undefined, true).then(
      (cart: ShoppingCartDto | undefined) => {
        if (!cart) {
          this.toastService.toastItemAdd(new ToastItem({
            type: EToastItemType.Danger,
            title: $localize`:@@CartPreviewItemDeleteInfoErrorTitle:Failure`,
            message: $localize`:@@CartPreviewItemDeleteInfoErrorMessage:Item could not be deleted from shopping cart.`,
            code: ErrorCodeService.formatErrorCode(EError.F170011)
          }));
          return;
        }

        if (ENVIRONMENT.cart.shouldValidateCarts != true) {
          return;
        }

        if (this.cartPreviewItem?.productIdentifier == undefined) {
          return;
        }
      }
    ).catch((ex) => {
      this.toastService.toastItemAdd(new ToastItem({
        type: EToastItemType.Danger,
        title: $localize`:@@CartPreviewItemDeleteInfoErrorTitle:Failure`,
        message: $localize`:@@CartPreviewItemDeleteInfoErrorMessage:Item could not be deleted from shopping cart.`,
        code: JSON.parse(ex.response).errorCode
      }));
    });
  }
}
